import { css } from '@linaria/core';
import 'antd/dist/reset.css';
import React from 'react';
import ReactDOM from 'react-dom/client';

import App from './App';
import { THEME_COLORS } from './config/theme';
import { initFrontendSentry } from './lib/observability/sentry';
import { preservePrerenderedPage } from './lib/utils/prerender';

const globalStyles = css`
  .ant-typography a {
    text-decoration: underline;
    text-underline-offset: 0.15em;
  }

  :global() {
    overscroll-behavior: none;

    html,
    body,
    #root {
      min-width: 300px;
      min-height: 100%;
    }

    html,
    body {
      font-family: 'Inter', sans-serif;
      font-synthesis: none;
    }

    body {
      --brand-primary: ${THEME_COLORS.PRIMARY};
      --brand-secondary: ${THEME_COLORS.SECONDARY};
      --brand-support: ${THEME_COLORS.PREMIUM};
      --brand-deep-blue: ${THEME_COLORS.DEEP_BLUE};
      --brand-gold: ${THEME_COLORS.GOLD};
      --brand-gold-hover: ${THEME_COLORS.GOLD_HOVER};
      --brand-gold-soft: ${THEME_COLORS.GOLD_SOFT};
      --brand-bronze: ${THEME_COLORS.BRONZE};
      --brand-metal: ${THEME_COLORS.METAL};
      --brand-deep-black: ${THEME_COLORS.DEEP_BLACK};
      --brand-night-brown: ${THEME_COLORS.NIGHT_BROWN};
      --brand-warm-brown: ${THEME_COLORS.WARM_BROWN};
      --brand-cream: ${THEME_COLORS.CREAM};
      --brand-neutral: ${THEME_COLORS.PRIMARY_SOFT};
      --brand-gradient: linear-gradient(
        135deg,
        ${THEME_COLORS.PRIMARY} 0%,
        ${THEME_COLORS.DEEP_BLUE} 62%,
        ${THEME_COLORS.SECONDARY} 100%
      );
      --brand-shadow: 0 8px 24px rgba(17, 24, 39, 0.08);
      --surface-elevated: ${THEME_COLORS.LIGHT.ELEVATED_SURFACE};
      --condition-mint: ${THEME_COLORS.SUCCESS};
      --condition-played: ${THEME_COLORS.WARNING};
      --condition-damaged: ${THEME_COLORS.ERROR};
      --rarity-premium: ${THEME_COLORS.PREMIUM};

      --custom-gray-1: #ffffff;
      --custom-gray-2: #f9fafb;
      --custom-gray-3: #f3f4f6;
      --custom-gray-4: #e5e7eb;
      --custom-gray-5: #d1d5db;
      --custom-gray-6: #9ca3af;
      --custom-gray-7: #6b7280;
      --custom-gray-8: #4b5563;
      --custom-gray-9: #374151;
      --custom-gray-10: #111827;

      margin: 0;
      background: var(--ant-color-bg-layout);
      color: var(--ant-color-text);
    }

    html[data-theme='dark'] body {
      --brand-neutral: #0a315f;
      --brand-shadow: 0 12px 32px rgba(0, 0, 0, 0.28);
      --surface-elevated: ${THEME_COLORS.DARK.ELEVATED_SURFACE};
      --custom-gray-1: #111113;
      --custom-gray-2: #18181b;
      --custom-gray-3: #202023;
      --custom-gray-4: #27272a;
      --custom-gray-5: #3f3f46;
      --custom-gray-6: #52525b;
      --custom-gray-7: #71717a;
      --custom-gray-8: #a1a1aa;
      --custom-gray-9: #d4d4d8;
      --custom-gray-10: #fafafa;
    }

    *,
    *::before,
    *::after {
      box-sizing: border-box;
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      font-family: 'Inter', sans-serif;
      font-weight: 700;
      letter-spacing: -0.02em;
    }

    .ant-btn {
      font-weight: 600;
    }

    .ant-form-item-label,
    .ant-menu-title-content,
    .ant-segmented-item-label {
      font-weight: 500;
    }

    .ant-descriptions-bordered .ant-descriptions-item-label {
      background: var(--ant-color-bg-container);
    }

    .ant-descriptions-bordered .ant-descriptions-item-content {
      background: var(--surface-elevated);
    }

    code,
    kbd,
    samp,
    .tcg-metadata {
      font-family: var(--ant-font-family-code);
      font-variant-numeric: tabular-nums;
    }

    @media (max-width: 600px) {
      .ant-modal {
        max-width: calc(100vw - 24px);
        margin: 12px auto;
        padding-bottom: 12px;
      }

      .ant-modal-content {
        padding: 16px;
      }

      .ant-modal-footer {
        display: flex;
        gap: 8px;
      }

      .ant-modal-footer > .ant-btn {
        flex: 1;
        margin-inline-start: 0;
      }

      .ant-drawer-content-wrapper {
        max-width: 100vw;
      }

      .ant-picker,
      .ant-input-number,
      .ant-select {
        max-width: 100%;
      }

      .ant-descriptions-bordered .ant-descriptions-row {
        display: flex;
        flex-direction: column;
      }

      .ant-descriptions-bordered .ant-descriptions-item-label,
      .ant-descriptions-bordered .ant-descriptions-item-content {
        display: block;
        width: 100%;
      }
    }
  }
`;

const rootEl = document.getElementById('root');

if (rootEl) {
  initFrontendSentry();
  import('./lib/observability/web-vitals')
    .then(({ initWebVitals }) => initWebVitals())
    .catch(() => undefined);
  rootEl.classList.add(globalStyles);
  preservePrerenderedPage(rootEl);
  const root = ReactDOM.createRoot(rootEl);

  root.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
}
